import type { ReactNode } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import ArticleHero from '../../../../components/news/ArticleHero';
import PullQuote from '../../../../components/news/PullQuote';
import KeyPointsBox from '../../../../components/news/KeyPointsBox';
import CommentBox from '../../../../components/news/CommentBox';
import SourceCard from '../../../../components/news/SourceCard';
import RelatedCards from '../../../../components/news/RelatedCards';

/* ---- In-article inline parts (editorial layout; no section bands) ---- */
function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}
function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        Source: {media} (<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>)
      </figcaption>
    </figure>
  );
}
function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}
const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function AppleRobotButlerPageEN() {
  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'News', href: '/en/news' },
    { label: 'Apple Robot Butler Development Project' }
  ];

  const article = {
    category: 'Technology & Innovation',
    date: 'April 4, 2024',
    title: 'Apple Robot Butler Development Project',
    subtitle: 'AI Technology Opening the Future of Butler Services',
    heroImage: '/images/generated/jba-fs-054-news_apple-robot-butler.webp',
    lead: 'Apple has revealed plans to develop an autonomous robot butler, utilizing personnel reassigned from the terminated automotive project to pursue AI-powered home mobile robots.\n\nThis innovative initiative is attracting attention as it brings new possibilities to the future of butler services.',
    sections: [
      {
        heading: 'Project Background and History',
        image: '/images/generated/jba-fs-055-news_apple-robot-butler-v3.webp', aspect: 'aspect-[3/4]',
        alt: 'A development laboratory advancing AI and robotics research',
        paragraphs: [
          'According to Bloomberg reports, Apple officially canceled its automotive project "Titan" in February 2024, reassigning approximately 2,000 employees to AI-related projects.',
          'This project had existed since at least 2014 but suffered from leadership changes and repeated delays. The company is now shifting resources to home mobile robots, considering the development of autonomous robot butlers.',
          'This initiative is positioned as part of Apple\'s strategy to invest more aggressively in AI products, following criticism that the company has fallen behind competitors like Google and Microsoft in generative AI.'
        ]
      },
      {
        heading: 'Expected Features and Technical Specifications',
        image: '/images/generated/jba-fs-056-news_apple-robot-butler-v3.webp', aspect: 'aspect-[3/4]',
        alt: 'A home robot navigating rooms and assisting with household tasks',
        paragraphs: [
          'The proposed robot is expected to use AI models to navigate between rooms and recognize its surroundings through cameras and other onboard sensors.',
          'Specifically, it would follow users around the home, perform fully autonomous household tasks such as cleaning and dishwashing, and function as a mobile video conferencing tool.',
          'However, many details remain unclear, including the method of movement (wheeled or bipedal), specific task execution methods, release date, price, and feasibility.',
          'Apple has a history of unrealized ideas such as AirPower and tablet prototypes, and this robot may face similar challenges.'
        ]
      },
      {
        heading: 'Industry Trends and Competitive Landscape',
        image: '/images/generated/jba-fs-057-news_apple-robot-butler-v3.webp', aspect: 'aspect-video',
        alt: 'A diversifying home robot industry from vacuum robots to humanoids',
        paragraphs: [
          'In the home robot market, iRobot has sold over 40 million Roomba autonomous vacuum cleaners worldwide, and Amazon is developing Astro, a home monitoring robot.',
          'Figure has integrated large language models into bipedal humanoid robots and partnered with BMW to perform tasks at their South Carolina manufacturing facility.',
          'Regarding consumer reactions, a 2021 Brookings Institution survey found that 61% of adults feel uncomfortable with robots, and a 2022 Pew Research survey revealed that over 37% of adults are "more concerned than excited" about increased AI use in daily life.'
        ]
      }
    ],
    comment: 'Apple\'s robot butler development initiative is noted as an extremely interesting sign of technological innovation for the butler service industry.\n\nOur association is carefully monitoring the impact of AI technology advancement on traditional butler services.\n\nWhile robot technology demonstrates usefulness in household efficiency and basic service provision, we believe that the core elements of true butler service—"omotenashi spirit," "situational judgment," and "human warmth"—are irreplaceable human values that technology cannot substitute.\n\nWe expect technology and human services to develop complementarily, contributing to higher quality hospitality, and will continue to improve our expertise toward the further development of butler services in Japan, referencing such technological trends.',
    source: [
      { label: 'Source', value: 'Popular Science', link: false },
      { label: 'Article URL', value: 'https://www.popsci.com/technology/apple-home-robot/', link: true },
      { label: 'Access Date', value: 'October 2025', link: false }
    ],
    related: [
      { to: '/en/news/downton-abbey-grand-finale', image: '/images/news-sources/downton-imdb.jpg', aspect: 'aspect-[3/4]', alt: 'IMDb title page showing the poster and film information for Downton Abbey: The Grand Finale', title: 'Downton Abbey: The Grand Finale Film Release', desc: 'Global attention as the culmination of butler culture' },
      { to: '/en/news/raffles-london-campaign', image: '/images/generated/seq-raffles-london-hero-7aaa64.webp', aspect: 'aspect-[3/4]', alt: 'Related article illustration of an illuminated London hotel exterior for the campaign sequel', title: 'Raffles "The Butler Did It" Campaign', desc: 'Luxury hotel butler service innovation' }
    ]
  };
  const S = article.sections;

  const sources = [
    { label: 'Source', value: 'Popular Science', link: false },
    { label: 'Original Article', value: 'https://www.popsci.com/technology/apple-home-robot/', link: true },
    { label: 'Tabletop Robot Report', value: 'https://www.macrumors.com/2025/08/13/apple-launching-robot-virtual-companion-in-2027/', link: true },
    { label: 'Release / Price', value: 'https://siliconangle.com/2025/08/13/apple-reportedly-targeting-2027-release-tabletop-robot-movable-display/', link: true },
    { label: 'Amazon Astro', value: 'https://en.wikipedia.org/wiki/Amazon_Astro', link: true },
    { label: 'Access Date', value: 'July 2026', link: false }
  ];
  const toc = [
    { id: 's-background', label: 'Project Background and History' },
    { id: 's-tabletop', label: 'The Reported "Tabletop Robot"' },
    { id: 's-specs', label: 'Expected Features and Technical Specifications' },
    { id: 's-industry', label: 'Industry Trends and Competitive Landscape' },
    { id: 's-rivals', label: 'Rival Home Robots: Astro and Ballie' }
  ];

  return (
    <Layout>
      <title>Apple Robot Butler Development Project | Japan Butler Association</title>
      <meta name="description" content="Apple plans to develop an autonomous robot butler using personnel reassigned from its terminated automotive project, pursuing AI-powered home mobile robots for the future of butler services." />
      <link rel="canonical" href="https://butler-association.jp/en/news/apple-robot-butler" />
      <link rel="alternate" hrefLang="ja" href="https://butler-association.jp/news/apple-robot-butler" />
      <link rel="alternate" hrefLang="en" href="https://butler-association.jp/en/news/apple-robot-butler" />

      <ArticleHero eyebrow="News / Technology" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <div className="space-y-4">
              {article.lead.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean).map((s, i) => (
                <p key={i} className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{s}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="Article Highlights"
              points={[
                'Apple is reportedly reassigning talent from its terminated car project to home robot development',
                'Reports describe a movable-display "tabletop robot" in development targeting 2027, with a rumored price around $1,000',
                'Rivals like Amazon Astro and Samsung Ballie exist, but commercializing home robots is hard even for major players'
              ]}
            />

            <SectionHeading id="s-background" en="Background">{S[0].heading}</SectionHeading>
            <div className="space-y-6">{S[0].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
            <SourceShot
              src="/images/news-sources/apple-popsci.jpg"
              alt="Screenshot of the Popular Science article on Apple's home robot"
              media="Popular Science"
              url="https://www.popsci.com/technology/apple-home-robot/"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="Technology can make chores efficient, but the spirit of omotenashi, situational judgment and human warmth cannot be replaced." cite="Japan Butler Association" />
            </div>

            <SectionHeading id="s-tabletop" en="Tabletop Robot">The Reported "Tabletop Robot"</SectionHeading>
            <div className="space-y-6">
              <div className="space-y-5">
                <p className={P}>According to reporting by Bloomberg's Mark Gurman and others, the device in development is a "tabletop robot" with an iPad-like display of about 7 inches mounted on a movable arm.</p>
                <p className={P}>The display rotates and tilts, automatically follows the speaker during FaceTime calls, and is positioned as an AI companion that also controls the smart home and monitors home security.</p>
              </div>
              <div className="space-y-5">
                <p className={P}>Reports put the target release around <Stat>2027</Stat>, with the price said to be aimed at roughly <Stat>$1,000</Stat>.</p>
                <p className={P}>Its internal codename is "J595," reportedly approved in 2022 with development since accelerating—all observations at the time of reporting, not confirmed facts.</p>
              </div>
            </div>
            <SourceShot
              src="/images/news-sources/apple-wikipedia.jpg"
              alt="Screenshot of the English Wikipedia page describing Apple Inc."
              media="Wikipedia (Apple Inc.)"
              url="https://en.wikipedia.org/wiki/Apple_Inc."
            />

            <SectionHeading id="s-specs" en="Specifications">{S[1].heading}</SectionHeading>
            <div className="space-y-6">{S[1].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-industry" en="Industry">{S[2].heading}</SectionHeading>
            <div className="space-y-6">{S[2].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-rivals" en="Rivals">Rival Home Robots: Astro and Ballie</SectionHeading>
            <div className="space-y-6">
              <div className="space-y-5">
                <p className={P}>Among shipping home robots, Amazon's "Astro" is a leading example.</p>
                <p className={P}>Alexa-enabled, it drives itself around the home to handle security monitoring and carrying items; announced in 2021, it is sold by invitation at a price of <Stat>about $1,599</Stat> (up from an initial $999).</p>
              </div>
              <div className="space-y-5">
                <p className={P}>Meanwhile Samsung's spherical AI robot "Ballie," announced repeatedly, missed its summer 2025 launch target and had still not shipped as of 2026.</p>
                <p className={P}>Home robots face a high bar to commercialization even for major players, and Apple's plan carries similar uncertainty.</p>
              </div>
            </div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="Contents" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">Reporting</dt><dd className="text-[#333] mt-0.5">Bloomberg (Mark Gurman)</dd></div>
                  <div><dt className="text-[#8f7a56]">Target Release</dt><dd className="text-[#333] mt-0.5">2027 (reported)</dd></div>
                  <div><dt className="text-[#8f7a56]">Est. Price</dt><dd className="text-[#333] mt-0.5">~$1,000 (rumored)</dd></div>
                  <div><dt className="text-[#8f7a56]">Codename</dt><dd className="text-[#333] mt-0.5">J595</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="Comment from Japan Butler Association" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="Source" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="Related Articles" items={article.related} readLabel="Read" backHref="/en/news" backLabel="Back to News List" />
    </Layout>
  );
}
